import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import Topbar from "@/components/Topbar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Lightning, Spinner, PaperPlaneTilt, ChatCircleDots, Phone, Sparkle, ArrowDownLeft } from "@phosphor-icons/react";
import { invoicesApi, aiApi, remindersApi, repliesApi } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/format";
import { toast } from "sonner";

export default function AgentPage() {
  const [sp] = useSearchParams();
  const preselect = sp.get("invoice");
  const [invoices, setInvoices] = useState([]);
  const [selectedId, setSelectedId] = useState(preselect || "");
  const [channel, setChannel] = useState("whatsapp");
  const [tone, setTone] = useState("gentle");
  const [script, setScript] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const [lastReply, setLastReply] = useState(null);

  useEffect(() => {
    invoicesApi.list({ status: "overdue" }).then((data) => {
      setInvoices(data);
      if (!selectedId && data.length) setSelectedId(data[0].id);
    });
  }, []);

  const selected = useMemo(() => invoices.find(i => i.id === selectedId), [invoices, selectedId]);

  const generate = async () => {
    if (!selected) return toast.error("Select an invoice");
    setLoading(true); setScript("");
    try {
      const r = await aiApi.generateScript({ customer_id: selected.customer_id, invoice_id: selected.id, channel, tone });
      setScript(r.message);
      toast.success("AI script generated");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI generation failed"); }
    finally { setLoading(false); }
  };

  const send = async () => {
    if (!script.trim()) return toast.error("Generate or write a script first");
    setSending(true);
    try {
      const r = await remindersApi.send({ customer_id: selected.customer_id, invoice_id: selected.id, channel, message: script });
      if (r.simulated) toast.success(`${channel === "whatsapp" ? "WhatsApp" : "Call"} simulated (Twilio not configured)`);
      else toast.success(`${channel === "whatsapp" ? "WhatsApp sent" : "Call initiated"}`);
      setScript("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const simulateReply = async () => {
    if (!replyText.trim() || !selected) return;
    setReplying(true); setLastReply(null);
    try {
      const r = await repliesApi.simulate({
        customer_id: selected.customer_id,
        invoice_id: selected.id,
        message: replyText,
      });
      setLastReply(r);
      toast.success(`AI classified reply as: ${r.classification?.toUpperCase()}`);
      setReplyText("");
      if (r.classification === "paid") {
        // Refresh overdue queue since invoice was auto-marked paid
        const data = await invoicesApi.list({ status: "overdue" });
        setInvoices(data);
        if (data.length) setSelectedId(data[0].id);
      }
    } catch (e) { toast.error("Reply simulation failed"); }
    finally { setReplying(false); }
  };

  return (
    <AppLayout>
      <Topbar
        title="AI Agent Command Center"
        subtitle="Generate, review, and dispatch intelligent payment reminders"
      />
      <div className="px-6 lg:px-10 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: overdue invoices list */}
        <div className="lg:col-span-1 border border-border bg-card rounded-md p-5 h-fit">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl font-semibold">Overdue Queue</h3>
            <Badge variant="secondary" className="font-mono">{invoices.length}</Badge>
          </div>
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {invoices.length === 0 && (
              <p className="text-sm text-muted-foreground py-6 text-center">No overdue invoices.</p>
            )}
            {invoices.map((i, idx) => (
              <button
                key={i.id}
                data-testid={`queue-item-${i.id}`}
                onClick={() => { setSelectedId(i.id); setScript(""); }}
                className={`w-full text-left p-3 rounded-md border transition-colors stagger-in ${
                  selectedId === i.id ? "bg-primary/5 border-primary" : "bg-background border-border hover:bg-muted"
                }`}
                style={{ animationDelay: `${idx * 30}ms` }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{i.customer_name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{i.invoice_number}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono tabular-nums text-sm font-semibold">{formatCurrency(i.amount, i.currency)}</div>
                    <div className="text-[11px] text-destructive font-mono">{i.days_overdue}d late</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Right: Agent control */}
        <div className="lg:col-span-2 space-y-6">
          {selected ? (
            <>
              <div className="border border-border bg-card rounded-md p-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h3 className="font-display text-2xl font-semibold">{selected.customer_name}</h3>
                    <div className="text-xs text-muted-foreground mt-0.5 font-mono">
                      {selected.customer_company || ""} · {selected.customer_phone}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono tabular-nums text-3xl font-semibold">{formatCurrency(selected.amount, selected.currency)}</div>
                    <div className="text-xs text-muted-foreground">Due {formatDate(selected.due_date)} · {selected.days_overdue}d overdue</div>
                  </div>
                </div>
              </div>

              <div className="border border-border bg-card rounded-md p-6 ai-glow">
                <div className="flex items-center gap-2.5 mb-5">
                  <Sparkle className="h-5 w-5 text-primary" weight="fill" />
                  <h3 className="font-display text-xl font-semibold">Compose Reminder</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-muted-foreground font-mono">Channel</label>
                    <Select value={channel} onValueChange={setChannel}>
                      <SelectTrigger data-testid="channel-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="whatsapp"><div className="flex items-center gap-2"><ChatCircleDots className="h-4 w-4" /> WhatsApp</div></SelectItem>
                        <SelectItem value="voice"><div className="flex items-center gap-2"><Phone className="h-4 w-4" /> Voice Call</div></SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-muted-foreground font-mono">Tone</label>
                    <Select value={tone} onValueChange={setTone}>
                      <SelectTrigger data-testid="tone-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gentle">Gentle · Day 1-3</SelectItem>
                        <SelectItem value="firm">Firm · Day 4-13</SelectItem>
                        <SelectItem value="final">Final Notice · Day 14+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button
                  data-testid="generate-script-button"
                  onClick={generate}
                  disabled={loading}
                  className="mt-5 bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  {loading ? <Spinner className="h-4 w-4 animate-spin mr-2" /> : <Lightning weight="fill" className="h-4 w-4 mr-2" />}
                  Generate with GPT 6 Sol
                </Button>

                <div className="mt-6">
                  <label className="text-xs uppercase tracking-wider text-muted-foreground font-mono">AI Script (editable)</label>
                  <Textarea
                    data-testid="script-textarea"
                    rows={8}
                    placeholder="Click 'Generate' to let AI draft a reminder, or write your own..."
                    value={script}
                    onChange={(e) => setScript(e.target.value)}
                    className="mt-1.5 font-mono text-sm"
                  />
                </div>

                <div className="flex items-center justify-end gap-2 mt-5">
                  <Button variant="outline" onClick={() => setScript("")} data-testid="clear-script-button">Clear</Button>
                  <Button
                    data-testid="send-reminder-button"
                    onClick={send}
                    disabled={sending || !script.trim()}
                    className="bg-primary text-primary-foreground"
                  >
                    {sending ? <Spinner className="h-4 w-4 animate-spin mr-2" /> : <PaperPlaneTilt weight="fill" className="h-4 w-4 mr-2" />}
                    {channel === "whatsapp" ? "Send WhatsApp" : "Place Call"}
                  </Button>
                </div>
              </div>

              <div className="border border-border bg-card rounded-md p-6">
                <div className="flex items-center gap-2.5 mb-4">
                  <ArrowDownLeft className="h-5 w-5 text-primary" weight="duotone" />
                  <h3 className="font-display text-xl font-semibold">Simulate Customer Reply</h3>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                  Test AI reply handling. The AI will classify the message into paid / promise / dispute / query / other.
                  In production, this is triggered automatically by the Twilio WhatsApp webhook.
                </p>
                <div className="flex items-stretch gap-2">
                  <input
                    data-testid="reply-text-input"
                    type="text"
                    placeholder='e.g. "Already paid yesterday" or "Can I pay next week?"'
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && simulateReply()}
                    className="flex-1 h-10 px-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <Button
                    data-testid="simulate-reply-button"
                    onClick={simulateReply}
                    disabled={replying || !replyText.trim()}
                    className="bg-primary text-primary-foreground"
                  >
                    {replying ? <Spinner className="h-4 w-4 animate-spin" /> : "Classify"}
                  </Button>
                </div>
                {lastReply && (
                  <div className="mt-4 p-4 rounded-md border border-border bg-background" data-testid="reply-result">
                    <div className="flex items-center gap-2">
                      <span className="text-xs uppercase tracking-wider font-mono text-muted-foreground">AI Verdict:</span>
                      <span className={`px-2 py-0.5 rounded-md text-[11px] font-mono uppercase font-semibold ${
                        lastReply.classification === "paid" ? "bg-success text-white" :
                        lastReply.classification === "promise" ? "bg-amber-500 text-white" :
                        lastReply.classification === "dispute" ? "bg-destructive text-destructive-foreground" :
                        "bg-muted text-foreground"
                      }`}>{lastReply.classification}</span>
                    </div>
                    {lastReply.classification_reason && (
                      <p className="text-sm text-foreground/85 mt-2">{lastReply.classification_reason}</p>
                    )}
                    {lastReply.classification === "paid" && (
                      <p className="text-xs text-success mt-2 font-medium">Invoice automatically marked as PAID.</p>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="border border-border bg-card rounded-md p-10 text-center">
              <p className="text-muted-foreground">Select an overdue invoice from the queue to begin.</p>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
